import { useEffect, useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "../../ui/table";
import Badge from "../../ui/badge/Badge";
import { Edit, Trash2, AlertCircle } from 'lucide-react';
import MoreButton from '../../common/MoreButton';
import { useNavigate, useParams } from "react-router";
import api from "../../../services/api";
import authService from "../../../services/authService";
import Pagination from "../../ui/pagination/Pagination";
import ComponentCard from "../../common/ComponentCard";
import Button from "../../ui/button/Button";
import Modal from '../../common/Modal';
import Alert from '../../ui/alert/Alert';

// Definição da interface da TheMembers
interface TheMembers {
    id: string;
    webhookId: string;
    companyId: string;
    name: string;
    url: string;
    productList: string[]; // Array de IDs de produtos
    productName: string;
    events: string[];
    active: boolean;
    type: string;
    settingsData: any;
    createdAt: string;
    updatedAt: string;
    createdById: string | null;
    products?: { // Detalhes dos produtos (pode ser adicionado pela API)
        id: string;
        name: string;
        imageUrl: string | null;
    }[];
    company?: {
        id: string;
        fantasyName: string;
    };
}

interface TheMembersTableProps {
    productId?: string;
}

export default function TheMembersTable({ productId }: TheMembersTableProps) {
    const navigate = useNavigate();
    const { id: paramId } = useParams<{ id: string }>();
    const productIdToUse = productId || paramId;
    const [theMembers, setTheMembers] = useState<TheMembers[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [alertInfo, setAlertInfo] = useState<{
        show: boolean;
        type: "success" | "error" | "warning" | "info";
        message: string;
        title: string;
    } | null>(null);

    // Estados para paginação
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
    const [webhookToDelete, setWebhookToDelete] = useState<string | null>(null);
    const [deleteLoading, setDeleteLoading] = useState<boolean>(false);

    useEffect(() => {
        const fetchWebhooks = async () => {
            try {
                setLoading(true);
                setError(null);

                // Buscando os webhooks da empresa
                const response = await api.get('/api/membership', {
                    headers: {
                        'Authorization': `Bearer ${authService.getToken()}`
                    },
                    params: {
                        provider: 'astronmembers',
                        // Opcionalmente filtra por productId se especificado
                        ...(productIdToUse && { productId: productIdToUse })
                    }
                });

                if (response.data && response.data.success) {
                    setTheMembers(response.data.data);
                } else {
                    setTheMembers([]);
                }
            } catch (err: any) {
                console.error("Erro ao buscar webhooks da empresa:", err);
                setError(err.message || "Erro ao carregar webhooks da empresa");
            } finally {
                setLoading(false);
            }
        };

        fetchWebhooks();
    }, [productIdToUse]);

    // Função para recarregar os dados
    const reloadWebhooks = async () => {
        try {
            setLoading(true);
            setError(null);

            // Buscando os webhooks
            const response = await api.get('/api/membership', {
                headers: {
                    'Authorization': `Bearer ${authService.getToken()}`
                },
                params: {
                    provider: 'astronmembers',
                    ...(productIdToUse && { productId: productIdToUse })
                }
            });

            if (response.data && response.data.success) {
                setTheMembers(response.data.data);
            } else {
                setTheMembers([]);
            }
        } catch (err: any) {
            console.error("Erro ao buscar webhooks da empresa", err);
            setError(err.message || "Erro ao carregar webhooks da empresa");
        } finally {
            setLoading(false);
        }
    };

    // Função para editar um webhook
    const handleEditWebhook = (webhookId: string) => {
        navigate(`/apps/astronmembers/edit/${webhookId}`);
    };

    // Função para mudar de página
    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };

    // Cálculo para paginação
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = theMembers.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(theMembers.length / itemsPerPage);

    const handleDeleteWebhook = async () => {
        if (!webhookToDelete) return;

        setDeleteLoading(true);
        try {
            await api.delete(
                `/api/membership/${webhookToDelete}`,
                {
                    headers: {
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                }
            );

            // Fechar o modal e limpar o estado
            setDeleteModalOpen(false);
            setWebhookToDelete(null);
            setDeleteLoading(false);

            // Recarregar os dados
            reloadWebhooks();

            // Mostrar mensagem de sucesso
            setAlertInfo({
                show: true,
                type: "success",
                title: "Sucesso!",
                message: "Integração excluída com sucesso."
            });

            // Esconder a mensagem após 3 segundos
            setTimeout(() => setAlertInfo(null), 3000);
        } catch (error) {
            console.error("Erro ao excluir integração:", error);
            setAlertInfo({
                show: true,
                type: "error",
                title: "Erro!",
                message: "Erro ao excluir integração. Tente novamente."
            });
        }
    };

    const openDeleteModal = (webhookId: string) => {
        setWebhookToDelete(webhookId);
        setDeleteModalOpen(true);
    };

    if (error) {
        return <div className="p-4 text-center dark:text-white/90">Erro: {error}</div>;
    }

    if (theMembers.length === 0 && !loading) {
        return (
            <ComponentCard title="Gerencie suas integrações">
                <div className="flex flex-col items-center justify-center">
                    <div className="p-8 text-center dark:text-white/90">
                        Você ainda não possui integrações AstronMembers cadastradas.
                    </div>
                    <Button
                        size="md"
                        variant="primary"
                        onClick={() => navigate(`/apps/astronmembers/new-astronmembers`)}
                    >
                        Adicionar Integração
                    </Button>
                </div>
            </ComponentCard>
        );
    }

    return (
        <ComponentCard title="Gerencie suas integrações">
            <div className="col-span-12 space-y-6">
                <div className="mb-6">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                        {/* Campo de busca - com largura limitada */}
                        <div className="relative w-full md:w-1/3 lg:w-1/4">
                            <button className="absolute -translate-y-1/2 left-4 top-1/2">
                                <svg
                                    className="fill-gray-500 dark:fill-gray-400"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M3.04175 9.37363C3.04175 5.87693 5.87711 3.04199 9.37508 3.04199C12.8731 3.04199 15.7084 5.87693 15.7084 9.37363C15.7084 12.8703 12.8731 15.7053 9.37508 15.7053C5.87711 15.7053 3.04175 12.8703 3.04175 9.37363ZM9.37508 1.54199C5.04902 1.54199 1.54175 5.04817 1.54175 9.37363C1.54175 13.6991 5.04902 17.2053 9.37508 17.2053C11.2674 17.2053 13.003 16.5344 14.357 15.4176L17.177 18.238C17.4699 18.5309 17.9448 18.5309 18.2377 18.238C18.5306 17.9451 18.5306 17.4703 18.2377 17.1774L15.418 14.3573C16.5365 13.0033 17.2084 11.2669 17.2084 9.37363C17.2084 5.04817 13.7011 1.54199 9.37508 1.54199Z"
                                        fill=""
                                    />
                                </svg>
                            </button>
                            <input
                                type="text"
                                placeholder="Pesquisar integração..."
                                className="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-200 bg-transparent py-2.5 pl-12 pr-4 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-800 dark:bg-gray-900 dark:bg-white/[0.03] dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
                            />
                        </div>

                        {/* Botão - diretamente ao lado do campo de busca */}
                        <Button
                            size="md"
                            variant="primary"
                            onClick={() => navigate(`/apps/astronmembers/new-astronmembers`)}
                        >
                            Adicionar Integração
                        </Button>
                    </div>
                </div>
            </div>
            <div className="overflow-hidden rounded-xl">
                <div className="max-w-full overflow-x-auto">
                    <Table>
                        {/* Table Header */}
                        <TableHeader className="border-b border-gray-100 dark:border-white/[0.05]">
                            <TableRow>
                                <TableCell
                                    isHeader
                                    className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                                >
                                    Nome
                                </TableCell>
                                <TableCell
                                    isHeader
                                    className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                                >
                                    Produto
                                </TableCell>
                                <TableCell
                                    isHeader
                                    className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                                >
                                    Status
                                </TableCell>
                                <TableCell
                                    isHeader
                                    className="px-5 py-3 font-medium text-gray-500 text-end text-theme-xs dark:text-gray-400 pr-8"
                                >
                                    Ações
                                </TableCell>
                            </TableRow>
                        </TableHeader>

                        {/* Table Body */}
                        <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                            {!loading && currentItems.map((webhook) => (
                                <TableRow key={webhook.id}>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        <div>
                                            <span className="block font-medium text-gray-800 text-theme-sm dark:text-white/90">
                                                {webhook.name}
                                            </span>
                                        </div>
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        <div>
                                            {webhook.productName}
                                        </div>
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        <Badge
                                            size="sm"
                                            color={webhook.active ? "success" : "error"}
                                        >
                                            {webhook.active ? "Ativo" : "Inativo"}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-end text-theme-sm dark:text-gray-400 pr-8">
                                        <div className="flex justify-end gap-2 relative">
                                            <MoreButton
                                                options={[
                                                    {
                                                        label: "Editar",
                                                        onClick: () => handleEditWebhook(webhook.id),
                                                        icon: <Edit className="size-4" />
                                                    },
                                                    {
                                                        label: "Excluir",
                                                        onClick: () => openDeleteModal(webhook.id),
                                                        icon: <Trash2 className="size-4" />,
                                                        className: "text-red-600 dark:text-red-400"
                                                    }
                                                ]}
                                            />
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    {!loading && totalPages > 1 && (
                        <div className="p-4 border-t border-gray-100 dark:border-white/[0.05]">
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    )}
                </div>
            </div>
            {alertInfo && alertInfo.show && (
                <div className="mt-4">
                    <Alert
                        variant={alertInfo.type}
                        title={alertInfo.title}
                        message={alertInfo.message}
                    />
                </div>
            )}
            {/* Modal de confirmação de exclusão */}
            <Modal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                size="sm"
                onConfirm={handleDeleteWebhook}
                confirmText="Excluir"
                cancelText="Cancelar"
                confirmButtonClass="bg-red-500 hover:bg-red-600 text-white"
                confirmLoading={deleteLoading}
            >
                <div className="flex flex-col items-center text-center py-4">
                    <AlertCircle className="h-16 w-16 text-red-500 mb-4" />
                    <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Tem certeza que deseja excluir esta integração?
                    </h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 max-w-sm">
                        Não será possível reverter esta ação!
                    </p>
                </div>
            </Modal>
        </ComponentCard>
    );
}